import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleFromPath,
  isValidLocale,
} from '@/lib/i18n-utils'

export const I18N_NAMESPACES = ['common', 'seo', 'landing', 'auth', 'blog', 'pages', 'lab', 'ledger']

const isClient = typeof window !== 'undefined'
const languageFromPath = isClient ? getLocaleFromPath(window.location.pathname) : DEFAULT_LOCALE
const initialLanguage = isValidLocale(languageFromPath) ? languageFromPath : DEFAULT_LOCALE

// No fetch backend — translations come exclusively from SSR.
// Server: loadTranslationsForSSR() populates the store via addResourceBundle().
// Client: router hydrate() callback injects dehydrated translations before React renders.
i18n
  .use(initReactI18next)
  .init({
    lng: initialLanguage,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    nonExplicitSupportedLngs: false,
    load: 'currentOnly',
    defaultNS: 'common',
    ns: I18N_NAMESPACES,
    fallbackNS: 'common',
    resources: {},
    ...(!isClient && { initAsync: false }),
    interpolation: { escapeValue: false },
    returnNull: false,
    returnEmptyString: false,
    react: { useSuspense: false, bindI18nStore: 'added' },
  })

// 'data': the file was read and parsed. 'missing': confirmed absent (404 from
// ASSETS, or no glob entry in dev) — a real, permanent gap. 'error': the read
// threw or the response wasn't ok/parseable — a transient failure that must
// NOT be treated as "no translation exists", or the locale gets marked loaded
// forever with a hole in it.
type NamespaceResult =
  | { status: 'data'; data: Record<string, unknown> }
  | { status: 'missing' }
  | { status: 'error' }

async function readNamespace(locale: string, ns: string): Promise<NamespaceResult> {
  try {
    const { getAssets } = await import('./server/assets-context')
    const assets = getAssets()
    // Use the ASSETS binding only in a real Worker; under vite dev use the glob loader.
    if (assets && !import.meta.env.DEV) {
      const res = await assets.fetch(new Request(`https://assets/locales/${locale}/${ns}.json`))
      if (res.status === 404) return { status: 'missing' }
      if (!res.ok) return { status: 'error' }
      return { status: 'data', data: (await res.json()) as Record<string, unknown> }
    }
    const { localeModules } = await import('./i18n-dev-locales')
    const loader = localeModules[`/public/locales/${locale}/${ns}.json`]
    if (!loader) return { status: 'missing' }
    const mod = (await loader()) as { default?: Record<string, unknown> }
    return { status: 'data', data: (mod.default ?? mod) as Record<string, unknown> }
  } catch (e) {
    console.error(`[i18n] Error loading ${locale}/${ns}:`, e)
    return { status: 'error' }
  }
}

// A locale is only added here once every namespace resolved to real data or a
// confirmed 404/missing-file for both its own read and (for non-default
// locales) the en-US gap-fill. Anything else — a transient error on either
// side — leaves the locale out, so the next request retries it. Own bundles
// are always added with overwrite=true, so a retry cleanly heals a previous
// partial load instead of leaving stale English behind.
const completedLocales = new Set<string>()

// Fill the shared store for one locale. Keys missing in that locale are filled
// from en-US *inside the locale's own bundle*, so SSR output and the dehydrated
// client store are identical (no raw keys on untouched locales).
async function fillStoreForLocale(locale: string): Promise<void> {
  if (locale !== DEFAULT_LOCALE) {
    // Load (or reuse the in-flight/completed load of) en-US first. Every
    // namespace file for en-US is then read from disk/ASSETS at most once per
    // process; every other locale gap-fills from the shared store below
    // instead of re-reading the en-US files itself.
    await loadTranslationsForSSR(DEFAULT_LOCALE)
  }

  let allHandled = true

  await Promise.all(
    I18N_NAMESPACES.map(async (ns) => {
      const own = await readNamespace(locale, ns)
      if (own.status === 'data') {
        i18n.addResourceBundle(locale, ns, own.data, true, true)
      } else if (own.status === 'error') {
        allHandled = false
      }

      if (locale === DEFAULT_LOCALE) return

      const fallback = i18n.store?.data?.[DEFAULT_LOCALE]?.[ns] as Record<string, unknown> | undefined
      if (fallback) {
        i18n.addResourceBundle(locale, ns, fallback, true, false)
      } else if (!completedLocales.has(DEFAULT_LOCALE)) {
        // en-US itself hasn't fully resolved this namespace (yet, or due to
        // its own transient error) — can't tell that apart from here, so be
        // conservative and retry this locale next request too.
        allHandled = false
      }
    }),
  )

  if (allHandled) completedLocales.add(locale)
}

const ssrLoadsInFlight = new Map<string, Promise<void>>()

/**
 * Load translations for SSR into the shared store. Never changes the language:
 * each request switches its own i18next clone (see router.tsx).
 * Client-side this is a no-op — translations arrive via router dehydration.
 */
export async function loadTranslationsForSSR(locale: string): Promise<void> {
  if (typeof window !== 'undefined') return
  const inFlight = ssrLoadsInFlight.get(locale)
  if (inFlight) return inFlight
  if (completedLocales.has(locale)) return

  const load = (async () => {
    try {
      await fillStoreForLocale(locale)
    } finally {
      ssrLoadsInFlight.delete(locale)
    }
  })()
  ssrLoadsInFlight.set(locale, load)
  await load
}

export default i18n
